"use client";

import React, { useEffect, useMemo } from "react";
import styles from "./page.module.css";
import { useLocale } from "next-intl";
import { HomePageDataV2APIResponse } from "./home-page-types";
import { useListingsCacheStore } from "@/global-cache/useListingsCacheStore";
import { ListingCardData } from "@/types/types";
import { useHomePageStoreV2 } from "./home-page-storeV2";
import type { ResolvedMarket } from "@/lib/markets/resolve-market";

import WhyChooseDoomum from "@/components-v2/home/why-choose-doomum";
import LifestyleSection from "@/components-v2/home/lifestyle-section";
import TopDestinations from "@/components-v2/home/global/top-destinations";
import PopularListings from "@/components-v2/home/global/popular-listings";
import RecommendedListings from "@/components-v2/home/global/recommended-listings";
import TrustedAgents from "@/components-v2/home/global/trusted-agents";
import MarketInsights from "@/components-v2/home/global/market-insights";
import ExploreBanner from "@/components-v2/home/global/explore-banner";
import CuratedCollections from "@/components-v2/home/local/curated-collections";
import ExploreBeyondBanner from "@/components-v2/home/local/explore-beyond-banner";
import CtaBanner from "@/components-v2/home/local/cta-banner";
import CategoriesStrip from "@/components-v2/home/categories-strip";
import HomeHero from "@/components-v2/home/home-hero";
import EditorsPicks from "@/components-v2/home/global/editors-picks";
import ExploreCountrySection from "@/components-v2/home/local/explore-country";

export default function HomeLayout({
  homePageData,
  userSub,
  lat,
  long,
  market,
}: {
  homePageData: HomePageDataV2APIResponse;
  userSub: string | null;
  lat: string | null;
  long: string | null;
  market: ResolvedMarket;
}) {
  const locale = useLocale();

  const {
    mostPopular,
    madeForYou,
    listingsLocale,
    loaded,
    userSub: storeUserSub,
    initState,
  } = useHomePageStoreV2();

  const cachedListings = useListingsCacheStore((state) => state.listings);
  const setCache = useListingsCacheStore((state) => state.setMultipleListings);

  const madeForYouListings: ListingCardData[] = useMemo(() => {
    return madeForYou.listings.map((id) => cachedListings[id]).filter(Boolean);
  }, [madeForYou.listings, cachedListings]);

  const mostPopularListings: ListingCardData[] = useMemo(() => {
    return mostPopular.listings.map((id) => cachedListings[id]).filter(Boolean);
  }, [mostPopular.listings, cachedListings]);

  const isGlobalMode = market.isGlobal;
  const marketCountryCode = market.isGlobal ? null : market.countryCode;
  const marketName = market.displayName;

  useEffect(() => {
    if (!loaded || locale !== listingsLocale || userSub !== storeUserSub) {
      initState(homePageData, locale, userSub, setCache);
    }
  }, [
    locale,
    homePageData,
    userSub,
    loaded,
    listingsLocale,
    storeUserSub,
    initState,
    setCache,
  ]);

  return (
    <main className={styles.main}>
      <HomeHero
        isGlobalMode={isGlobalMode}
        marketName={marketName}
        marketHost={market.domain}
        marketCountryCode={marketCountryCode}
        heroImage={market.heroImage}
        lat={lat}
        long={long}
      />

      <div className={styles.contentContainer}>
        <CategoriesStrip />
        {isGlobalMode ? <LifestyleSection /> : <ExploreCountrySection />}
        <WhyChooseDoomum />
      </div>

      {isGlobalMode ? (
        <>
          <TopDestinations />
          <PopularListings listings={mostPopularListings} />
          <EditorsPicks />
          <RecommendedListings listings={madeForYouListings} />
          <TrustedAgents />
          <MarketInsights />
          <ExploreBanner />
        </>
      ) : (
        <>
          <PopularListings listings={mostPopularListings} />
          <RecommendedListings listings={madeForYouListings} />
          <TrustedAgents />
          <CuratedCollections />
          <ExploreBeyondBanner />
          <CtaBanner />
        </>
      )}
    </main>
  );
}
